import { defaultModeName } from "../../../api/modes";
import { GameMode } from "../../../api/types";
import type { CreateRoomInit, MapId, ModeRules, RoomRuleOverrides } from "../../../api/types";

export const NAME_MIN = 3;
export const NAME_MAX = 32;
export const PASSWORD_MAX = 32;
export const PLAYERS_MIN = 2;
export const PLAYERS_MAX = 16;

/** Map that is only offered for training; the default map of a new lobby is another one. */
export const PRACTICE_MAP_ID: MapId = "proving_grounds";

/** The overrides of `RoomRuleOverrides` the lobby form edits (`friendlyFire` is not part of it and is never sent). */
export type RuleKey = Exclude<keyof RoomRuleOverrides, "friendlyFire" | "$typeName" | "$unknown">;

/** Rule values that differ from the mode defaults, in request units (seconds for the time limit). */
export type RuleOverrides = Partial<Record<RuleKey, number>>;

export interface RuleSpec {
  label: string;
  /** Inclusive range in display units. */
  min: number;
  max: number;
  /** Whole numbers only (the time limit also accepts fractions of a minute). */
  integer: boolean;
  /** Request value = display value * scale (minutes -> seconds). */
  scale: number;
}

/** The server enforces its own ranges; these keep obviously wrong input from being sent. */
export const RULE_SPECS: Record<RuleKey, RuleSpec> = {
  scoreLimit: { label: "Score limit", min: 1, max: 999, integer: true, scale: 1 },
  timeLimitSec: { label: "Time limit (minutes)", min: 1, max: 60, integer: false, scale: 60 },
  roundsToWin: { label: "Rounds to win", min: 1, max: 15, integer: true, scale: 1 },
};

/** Overrides that apply to a mode, in display order. Defusal has no match clock and no score limit. */
export const MODE_RULE_KEYS: Record<GameMode, readonly RuleKey[]> = {
  [GameMode.UNSPECIFIED]: [],
  [GameMode.FFA]: ["scoreLimit", "timeLimitSec"],
  [GameMode.TDM]: ["scoreLimit", "timeLimitSec"],
  [GameMode.KOTH]: ["scoreLimit", "timeLimitSec"],
  [GameMode.DEFUSAL]: ["roundsToWin"],
};

/** Text of the rule inputs the user has edited; a missing key means "mode default". */
export type RuleDrafts = Partial<Record<RuleKey, string>>;

export interface LobbyForm {
  name: string;
  mode: GameMode;
  map: MapId;
  maxPlayers: number;
  /** Blank = no password. */
  password: string;
  listed: boolean;
  rules: RuleDrafts;
}

export interface LobbyErrors {
  name?: string;
  password?: string;
  maxPlayers?: string;
  rules: Partial<Record<RuleKey, string>>;
}

export function lobbyHasErrors(errors: LobbyErrors): boolean {
  return (
    errors.name !== undefined ||
    errors.password !== undefined ||
    errors.maxPlayers !== undefined ||
    Object.keys(errors.rules).length > 0
  );
}

/** Mode default of a rule in display units (minutes for the time limit). */
export function ruleDefault(rules: ModeRules, key: RuleKey): number {
  return rules[key] / RULE_SPECS[key].scale;
}

/** Default player cap of a mode, clamped to what a lobby may host. */
export function defaultMaxPlayers(rules: ModeRules): number {
  return Math.min(PLAYERS_MAX, Math.max(PLAYERS_MIN, rules.maxPlayers));
}

/** Error text for one rule input, or null when the text is a valid value. */
export function validateRuleText(key: RuleKey, text: string): string | null {
  const spec = RULE_SPECS[key];
  const trimmed = text.trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return "Enter a number";
  const value = Number(trimmed);
  if (spec.integer && !Number.isInteger(value)) return "Whole numbers only";
  if (value < spec.min || value > spec.max) return `Must be ${spec.min}-${spec.max}`;
  return null;
}

export function validateLobby(form: LobbyForm): LobbyErrors {
  const errors: LobbyErrors = { rules: {} };
  const nameLength = form.name.trim().length;
  if (nameLength < NAME_MIN) errors.name = `At least ${NAME_MIN} characters`;
  else if (nameLength > NAME_MAX) errors.name = `At most ${NAME_MAX} characters`;
  if (form.password.length > PASSWORD_MAX) errors.password = `At most ${PASSWORD_MAX} characters`;
  if (!Number.isInteger(form.maxPlayers) || form.maxPlayers < PLAYERS_MIN || form.maxPlayers > PLAYERS_MAX) {
    errors.maxPlayers = `Choose ${PLAYERS_MIN}-${PLAYERS_MAX} players`;
  }
  for (const key of MODE_RULE_KEYS[form.mode]) {
    const text = form.rules[key];
    if (text === undefined) continue;
    const error = validateRuleText(key, text);
    if (error !== null) errors.rules[key] = error;
  }
  return errors;
}

/** Only the values that differ from the mode defaults; undefined when nothing was changed. Input must be valid. */
export function buildSettings(mode: GameMode, defaults: ModeRules, drafts: RuleDrafts): RuleOverrides | undefined {
  const settings: RuleOverrides = {};
  for (const key of MODE_RULE_KEYS[mode]) {
    const text = drafts[key];
    if (text === undefined) continue;
    const value = Math.round(Number(text.trim()) * RULE_SPECS[key].scale);
    if (value !== defaults[key]) settings[key] = value;
  }
  return Object.keys(settings).length > 0 ? settings : undefined;
}

/**
 * Request for a valid form; a blank password and unchanged rules stay undefined.
 * `defaults` are the rules of the selected mode.
 */
export function buildCreateRequest(form: LobbyForm, defaults: ModeRules): CreateRoomInit {
  return {
    name: form.name.trim(),
    mode: form.mode,
    map: form.map,
    maxPlayers: form.maxPlayers,
    listed: form.listed,
    password: form.password !== "" ? form.password : undefined,
    settings: buildSettings(form.mode, defaults, form.rules),
  };
}

/** "6v6 · first to 75 kills · 10 min", derived from the rules of a mode. */
export function modeRulesSummary(rules: ModeRules): string {
  const parts = [rules.teamSize > 0 ? `${rules.teamSize}v${rules.teamSize}` : defaultModeName(rules.mode)];
  if (rules.roundsToWin > 0) parts.push(`first to ${rules.roundsToWin} rounds`);
  else if (rules.scoreLimit > 0) {
    const unit = rules.mode === GameMode.KOTH ? "points" : "kills";
    parts.push(`first to ${rules.scoreLimit} ${unit}`);
  }
  if (rules.timeLimitSec > 0) parts.push(`${Number((rules.timeLimitSec / 60).toFixed(1))} min`);
  return parts.join(" · ");
}

/** One line for the footer: what pressing "Create lobby" will make. `defaults` are the rules of the selected mode. */
export function describeLobby(form: LobbyForm, defaults: ModeRules, names: { mode: string; map: string }): string {
  const effective: ModeRules = { ...defaults, ...buildSettings(form.mode, defaults, form.rules) };
  const parts = [
    `${names.mode} on ${names.map}`,
    `up to ${form.maxPlayers} players`,
    modeRulesSummary(effective),
    form.listed ? "listed" : "unlisted",
  ];
  if (form.password !== "") parts.push("password protected");
  return `Creates: ${parts.join(" · ")}`;
}
